import { Fragment, useMemo, useState } from 'react'
import { Box, CircularProgress } from '@mui/material'
import dayjs from 'dayjs'
import { L, Icon, LavDropdown, useLavMobile } from '../ui/LavDropdown'
import { LavSectionTitle } from '../add-appointment/appointmentLavenderFields'
import DropdownCalendar from '../ui/DropdownCalendar'
import { MbIc } from './mbBits'
import { WARN_IC, WARN_BG } from './apdShared'
import {
	APR_NEXT_HORIZON,
	addDays,
	dayHours,
	daySlots,
	fmtClock,
	fmtFull,
	fmtJump,
	groomerEmpIds,
	groomerOptions,
	nextOpenings,
	openCount,
	timeToMins,
	todayStr,
	useBusyBookings,
	weekdayName,
} from './rescheduleShared'

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const NONE_COLOR = '#C7C1DB'

/**
 * RescheduleSlotPicker — the picker from the Claude Design reschedule flow:
 * groomer · suggested openings · a week strip + open slots for the chosen day.
 * Shows what is actually open for this appointment's duration and groomers;
 * busy and after-hours times stay reachable behind a link and warn instead
 * of block. Tapping a slot hands the pick (with its verdict) to the host.
 *
 * @param {Object} merchant - merchant row (timezone, weekly_hours, closed dates, name)
 * @param {Object[]} employees - active employees ({ id, first_name, last_name, services })
 * @param {string[]} bookedIds - groomer ids currently on the visit (unique)
 * @param {string[]} serviceIds - service ids on the visit (groomer options must cover all)
 * @param {string[]} excludeIds - appointment ids being moved (not counted as busy)
 * @param {number} mins - total duration of the visit
 * @param {{date, time}} current - the booked slot (merchant-local)
 * @param {{date, time}|null} value - the pending pick, if any
 * @param {Function} onPick - ({ date, time, mins, groomer, empIds, check }) => void
 */
export default function RescheduleSlotPicker({
	merchant,
	employees = [],
	bookedIds = [],
	serviceIds = [],
	excludeIds = [],
	mins,
	current = null,
	value = null,
	onPick,
}) {
	const mobile = useLavMobile()
	const tz = merchant?.timezone || 'UTC'
	const [groomer, setGroomer] = useState(bookedIds.length ? 'booked' : (employees[0]?.id || 'booked'))
	const empIds = groomerEmpIds(groomer, bookedIds)
	// Rescheduling into the past makes no sense: a visit whose date has gone
	// by anchors on today (default day + "Suggested openings" scan from today, and
	// only after the current clock time), otherwise on its own date.
	const today = todayStr(tz)
	const now = dayjs().tz(tz)
	const nowMins = now.hour() * 60 + now.minute()
	const apptDate = current ? current.date : (value ? value.date : today)
	const anchor = apptDate < today ? today : apptDate
	const afterMins = anchor === today
		? Math.max(nowMins, current && current.date === today ? timeToMins(current.time) : -1)
		: (current && current.date === anchor ? timeToMins(current.time) : null)
	const [day, setDay] = useState(value ? value.date : anchor)
	const [showAll, setShowAll] = useState(false)

	const dow = dayjs(day).day()
	const weekStart = addDays(day, -dow)
	const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart])

	// Bookings for the visible week plus the "suggested openings" scan window.
	const rangeFrom = weekStart < anchor ? weekStart : anchor
	const rangeToA = addDays(anchor, APR_NEXT_HORIZON)
	const rangeToB = addDays(weekStart, 6)
	const rangeTo = rangeToA > rangeToB ? rangeToA : rangeToB
	const { busyByDate, loading, loaded } = useBusyBookings({ merchantId: merchant?.id, tz, excludeIds, from: rangeFrom, to: rangeTo })
	const ready = Boolean(loaded)

	const ctx = { mins, empIds, busyByDate, merchant, employees }
	const next = useMemo(() => (ready
		? nextOpenings({ fromDate: anchor, afterMins, ...ctx })
		: []),
	// eslint-disable-next-line react-hooks/exhaustive-deps
	[ready, anchor, afterMins, mins, groomer, busyByDate, merchant, employees])
	const slots = useMemo(() => daySlots({ ds: day, all: showAll, ...ctx }),
	// eslint-disable-next-line react-hooks/exhaustive-deps
	[day, showAll, mins, groomer, busyByDate, merchant, employees])
	const openN = slots.filter((s) => s.kind === 'open').length
	const groups = [['Morning', 0, 12 * 60], ['Afternoon', 12 * 60, 18 * 60], ['Evening', 18 * 60, 24 * 60]]
		.map(([label, a, b]) => ({ label, slots: slots.filter((s) => s.mins >= a && s.mins < b) }))
		.filter((g) => g.slots.length)
	const H = dayHours(merchant, day)
	const closedDay = H.closed
	const isCur = (ds, t) => !!current && current.date === ds && current.time === t
	const isOn = (ds, t) => !!value && value.date === ds && value.time === t
	const pick = (ds, s) => onPick({
		date: ds, time: s.time, mins: s.mins, groomer, empIds,
		check: { kind: s.kind, cap: s.cap, conflicts: s.conflicts, note: s.note },
	})

	const gOptions = useMemo(() => groomerOptions(bookedIds, employees, serviceIds), [bookedIds, employees, serviceIds])

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: mobile ? '20px' : '18px', fontFamily: L.font }}>
			{/* ── groomer ── */}
			<Box component="section">
				<LavSectionTitle>Groomer</LavSectionTitle>
				<LavDropdown
					icon={<Icon name="user" size={15} />}
					placeholder="Pick a groomer"
					value={groomer}
					options={gOptions}
					onSelect={(id) => setGroomer(id || 'booked')}
				/>
			</Box>

			{/* ── suggested openings ── */}
			<Box component="section">
				<LavSectionTitle hint={`${mins} mins`}>Suggested openings</LavSectionTitle>
				{!ready ? (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', p: '8px 0', color: L.muted, fontSize: mobile ? '13px' : '12.5px', fontWeight: 600 }}>
						<CircularProgress size={14} sx={{ color: L.primary }} />
						<span>Checking the calendar…</span>
					</Box>
				) : next.length ? (
					<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px' }}>
						{next.map((o) => {
							const on = isOn(o.date, o.time)
							return (
								<Box
									component="button"
									type="button"
									key={o.date + o.time}
									onClick={() => pick(o.date, o)}
									sx={{
										display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
										gap: '2px', height: mobile ? 62 : 56, px: '6px',
										border: `1.5px solid ${on ? L.primary : L.border}`, borderRadius: mobile ? '13px' : L.rSm,
										bgcolor: on ? L.primarySoft : L.field, fontFamily: 'inherit', cursor: 'pointer',
										transition: 'border-color .12s, background .12s',
										'&:hover': { borderColor: L.primary },
									}}
								>
									<Box component="span" sx={{ fontSize: mobile ? '12.5px' : '11.5px', fontWeight: 650, color: on ? L.primaryDeep : L.muted, whiteSpace: 'nowrap' }}>
										{fmtFull(o.date)}
									</Box>
									<Box component="strong" sx={{ fontSize: mobile ? '15.5px' : '14px', fontWeight: 750, color: on ? L.primaryDeep : L.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
										{fmtClock(o.mins)}
									</Box>
								</Box>
							)
						})}
					</Box>
				) : (
					<EmptyNote mobile={mobile} title="Nothing open in the next 3 weeks">
						Try another groomer, or pick a day below and show busy times.
					</EmptyNote>
				)}
			</Box>

			{/* ── pick a slot ── */}
			<Box component="section">
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', mb: '8px', '& > div:first-of-type': { mb: 0 } }}>
					<LavSectionTitle>Pick a slot</LavSectionTitle>
					<Box sx={{ width: mobile ? 142 : 128, flex: 'none' }}>
						<DropdownCalendar value={day} onChange={setDay} small align="right" fmt={(d) => fmtJump(d)} />
					</Box>
				</Box>

				{/* week strip */}
				<Box sx={{ display: 'flex', alignItems: 'stretch', gap: '3px', mb: '12px' }}>
					<WeekNav mobile={mobile} label="Previous week" onClick={() => setDay(addDays(day, -7))}><MbIc name="chevL" size={14} sw={2} /></WeekNav>
					{days.map((ds) => {
						const d = dayjs(ds)
						const none = ready && !openCount({ ds, ...ctx })
						const sel = ds === day
						const cur = !!current && current.date === ds
						return (
							<Box
								component="button"
								type="button"
								key={ds}
								aria-label={fmtFull(ds)}
								aria-pressed={sel}
								onClick={() => setDay(ds)}
								sx={{
									position: 'relative', flex: '1 1 0', minWidth: 0, height: mobile ? 58 : 52, border: 'none',
									borderRadius: mobile ? '12px' : L.rSm, bgcolor: sel ? L.primary : 'transparent',
									fontFamily: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center',
									justifyContent: 'center', gap: '1px', p: 0, cursor: 'pointer', transition: 'background .12s',
									'&:hover': { bgcolor: sel ? L.primary : L.surface },
									...(cur ? {
										'&::after': {
											content: '""', position: 'absolute', bottom: 5, left: '50%', transform: 'translateX(-50%)',
											width: 4, height: 4, borderRadius: '50%', bgcolor: sel ? 'rgba(255, 255, 255, 0.85)' : L.primary,
										},
									} : {}),
								}}
							>
								<Box component="span" sx={{
									fontSize: mobile ? '10.5px' : '10px', fontWeight: 750, letterSpacing: '0.05em', textTransform: 'uppercase',
									color: sel ? L.onPrimary : none ? NONE_COLOR : L.muted,
								}}>
									{DOW[d.day()]}
								</Box>
								<Box component="strong" sx={{
									fontSize: mobile ? '16px' : '14.5px', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
									color: sel ? L.onPrimary : none ? NONE_COLOR : L.text,
								}}>
									{d.date()}
								</Box>
							</Box>
						)
					})}
					<WeekNav mobile={mobile} label="Next week" onClick={() => setDay(addDays(day, 7))}><MbIc name="chevR" size={14} sw={2} /></WeekNav>
				</Box>

				{!ready ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: '18px 0' }}>
						<CircularProgress size={20} sx={{ color: L.primary }} />
					</Box>
				) : closedDay && !showAll ? (
					<EmptyNote mobile={mobile} title={`Closed on ${weekdayName(day)}s`}>
						Pick another day, or show after-hours times to book outside opening hours.
					</EmptyNote>
				) : !slots.length ? (
					<EmptyNote mobile={mobile} title={`Nothing open for ${mins} mins on ${fmtFull(day)}`}>
						Try another day or groomer, or show busy times to squeeze it in.
					</EmptyNote>
				) : (
					<>
						<Box sx={{ fontSize: mobile ? '13.5px' : '12.5px', fontWeight: 650, color: L.muted, mb: '8px' }}>
							{openN
								? <><Box component="strong" sx={{ color: L.text, fontWeight: 750 }}>{openN} open</Box> on {fmtFull(day)}</>
								: <><Box component="strong" sx={{ color: L.text, fontWeight: 750 }}>Nothing open</Box> on {fmtFull(day)}</>}
							{current && current.date === day && (
								<Box component="em" sx={{ fontStyle: 'normal' }}> · booked now at {fmtClock(timeToMins(current.time))}</Box>
							)}
						</Box>
						<Box>
							{groups.map((g, gi) => (
								<Fragment key={g.label}>
									<Box sx={{
										fontSize: '10px', fontWeight: 750, letterSpacing: '0.07em', textTransform: 'uppercase',
										color: L.muted, m: gi === 0 ? '0 0 6px 2px' : '10px 0 6px 2px',
									}}>
										{g.label}
									</Box>
									<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '7px' }}>
										{g.slots.map((s) => (
											<SlotButton
												key={s.time}
												mobile={mobile}
												slot={s}
												cur={isCur(day, s.time)}
												on={isOn(day, s.time)}
												onClick={() => pick(day, s)}
											/>
										))}
									</Box>
								</Fragment>
							))}
						</Box>
					</>
				)}

				<Box
					component="button"
					type="button"
					onClick={() => setShowAll(!showAll)}
					sx={{
						border: 'none', bgcolor: 'transparent', color: L.primaryDeep, fontFamily: 'inherit',
						fontSize: mobile ? '14px' : '12.5px', fontWeight: 700, borderRadius: '8px',
						mt: '12px', ml: mobile ? '-10px' : '-8px', p: mobile ? '10px 10px' : '6px 8px', cursor: 'pointer',
						'&:hover': { bgcolor: L.primarySoft },
					}}
				>
					{showAll ? 'Hide busy & after-hours times' : 'Show busy & after-hours times'}
				</Box>
				{showAll && (
					<Box sx={{ fontSize: mobile ? '13px' : '11.5px', color: L.muted, fontWeight: 600, mt: '2px', lineHeight: 1.45, textWrap: 'pretty' }}>
						{closedDay
							? `Dimmed times are booked or on a day the shop is closed. You can still pick one. You’ll get a warning before anything moves.`
							: `Dimmed times are booked or outside ${fmtClock(H.open)} – ${fmtClock(H.close)}. You can still pick one. You’ll get a warning before anything moves.`}
					</Box>
				)}
				{loading && ready && (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '8px', color: L.muted, fontSize: '11.5px', fontWeight: 600 }}>
						<CircularProgress size={11} sx={{ color: L.muted }} />
						<span>Updating…</span>
					</Box>
				)}
			</Box>
		</Box>
	)
}

/* ── one slot (design .apr-slot) ── */
function SlotButton({ slot, cur, on, onClick, mobile }) {
	const dim = slot.kind !== 'open'
	return (
		<Box
			component="button"
			type="button"
			disabled={cur}
			onClick={onClick}
			sx={{
				height: mobile ? 50 : 44, border: '1.5px solid', borderStyle: dim && !on && !cur ? 'dashed' : 'solid',
				borderColor: on ? L.primary : cur ? L.primarySoft : L.border,
				borderRadius: mobile ? '12px' : L.rSm,
				bgcolor: on ? L.primary : cur ? L.primarySoft : dim ? 'transparent' : L.field,
				fontFamily: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
				gap: '2px', color: on ? L.onPrimary : cur ? L.primaryDeep : dim ? L.muted : L.text,
				fontSize: mobile ? '14.5px' : '13px', fontWeight: on ? 750 : dim ? 600 : 650,
				fontVariantNumeric: 'tabular-nums', px: '4px', cursor: cur ? 'default' : 'pointer',
				transition: 'border-color .12s, background .12s, color .12s',
				'&:hover': cur ? {} : dim && !on
					? { borderColor: WARN_IC, color: L.text, bgcolor: WARN_BG }
					: { borderColor: L.primary },
				'& small': {
					fontSize: mobile ? '10px' : '9.5px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', lineHeight: 1,
					color: on ? 'rgba(255, 255, 255, 0.85)' : cur ? L.primary : slot.kind === 'busy' ? WARN_IC : L.muted,
				},
			}}
		>
			<span>{fmtClock(slot.mins)}</span>
			{cur ? <small>Current</small> : slot.cap ? <small>{slot.cap}</small> : null}
		</Box>
	)
}

function WeekNav({ onClick, label, children, mobile }) {
	return (
		<Box
			component="button"
			type="button"
			aria-label={label}
			onClick={onClick}
			sx={{
				width: mobile ? 26 : 24, flex: 'none', border: 'none', borderRadius: '8px', bgcolor: 'transparent',
				color: L.muted, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0, cursor: 'pointer',
				'&:hover': { bgcolor: L.surface, color: L.text },
			}}
		>
			{children}
		</Box>
	)
}

function EmptyNote({ title, children, mobile }) {
	return (
		<Box sx={{
			fontSize: mobile ? '13px' : '12.5px', color: L.muted, fontWeight: 600, lineHeight: 1.45,
			bgcolor: L.surface, borderRadius: mobile ? '12px' : L.rSm, p: mobile ? '12px 14px' : '11px 13px', textWrap: 'pretty',
		}}>
			<Box component="strong" sx={{ display: 'block', color: L.text, fontWeight: 750, mb: '2px' }}>{title}</Box>
			{children}
		</Box>
	)
}
